import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import {
  strapiEnsureSamplingScheduleForQuotation,
  strapiGetQuotation,
  type StrapiSamplingScheduleRow,
} from '../../api/strapiCrm';

type GroupScheduleRow = {
  groupId: string;
  customerName: string;
  scheduleId: string;
  workOrderId: string | null;
  approvedDatesLabel: string | null;
  approvedByAdminSampling: boolean;
};

type Props = {
  open: boolean;
  jwt: string | null;
  quotationDocumentId: string;
  onClose: () => void;
  onEnsured?: () => void;
};

function scheduleApprovedByAdminSampling(schedule: StrapiSamplingScheduleRow): boolean {
  if (String(schedule.admin_sampling_completed_at ?? '').trim()) return true;
  if (String(schedule.approved_at ?? '').trim()) return true;
  return schedule.progress_status === 'approved';
}

function formatCalendarDate(value: string): string {
  const day = value.trim().slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return value.trim();
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

function approvedScheduleDatesLabel(schedule: StrapiSamplingScheduleRow): string | null {
  const dates = [...new Set((schedule.customer_agreed_dates ?? []).map((date) => date.trim().slice(0, 10)))]
    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
    .sort();
  if (dates.length) return dates.map(formatCalendarDate).join(', ');
  const start = String(schedule.customer_agreed_start_date ?? '').trim();
  const end = String(schedule.customer_agreed_end_date ?? '').trim();
  if (start && end) return `${formatCalendarDate(start)} – ${formatCalendarDate(end)}`;
  if (start) return formatCalendarDate(start);
  return null;
}

function workOrderLabel(schedule: StrapiSamplingScheduleRow): string | null {
  const jobNumber = String(schedule.work_order?.job_number ?? '').trim();
  const documentId = String(schedule.work_order?.documentId ?? '').trim();
  if (jobNumber && documentId) return `${jobNumber} (${documentId})`;
  return jobNumber || documentId || null;
}

export function ConsultantScheduleGroupDialog({
  open,
  jwt,
  quotationDocumentId,
  onClose,
  onEnsured,
}: Props) {
  const navigate = useNavigate();
  const onEnsuredRef = useRef(onEnsured);
  onEnsuredRef.current = onEnsured;
  const [rows, setRows] = useState<GroupScheduleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (!jwt || !quotationDocumentId) {
      setRows([]);
      setError('Sesi tidak ditemukan.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);
    void (async () => {
      try {
        const detail = await strapiGetQuotation(jwt, quotationDocumentId);
        const groups = (detail.quotation_groups ?? []).filter((group) => group.documentId?.trim());
        if (!groups.length) {
          if (!cancelled) setError('Quotation konsultan ini belum punya group pelanggan.');
          return;
        }
        const next: GroupScheduleRow[] = [];
        for (const [index, group] of groups.entries()) {
          const groupId = group.documentId.trim();
          const schedule = await strapiEnsureSamplingScheduleForQuotation(jwt, quotationDocumentId, groupId);
          const scheduleId = String(schedule.documentId ?? '').trim();
          if (!scheduleId) {
            throw new Error(`Gagal membuat penjadwalan untuk pelanggan ${index + 1}.`);
          }
          const approvedByAdminSampling = scheduleApprovedByAdminSampling(schedule);
          next.push({
            groupId,
            customerName: group.customer?.name?.trim() || `Pelanggan ${index + 1}`,
            scheduleId,
            workOrderId: workOrderLabel(schedule),
            approvedDatesLabel: approvedByAdminSampling ? approvedScheduleDatesLabel(schedule) : null,
            approvedByAdminSampling,
          });
        }
        if (cancelled) return;
        setRows(next);
        onEnsuredRef.current?.();
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Gagal menyiapkan penjadwalan per pelanggan.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, jwt, quotationDocumentId]);

  function openSchedule(scheduleId: string) {
    onClose();
    navigate(`/sampling/schedules/${encodeURIComponent(scheduleId)}`);
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Penjadwalan sampling</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Satu pelanggan punya satu jadwal. Pilih pelanggan untuk membuka penjadwalannya.
        </Typography>
        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Menyiapkan jadwal per pelanggan…</Typography>
          </Stack>
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!loading && rows.length > 0 ? (
          <List disablePadding>
            {rows.map((row) => (
              <ListItemButton
                key={row.groupId}
                onClick={() => openSchedule(row.scheduleId)}
                data-testid={`consultant-schedule-group-${row.groupId}`}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  alignItems: 'flex-start',
                }}
              >
                <Stack spacing={0.5} sx={{ width: '100%', py: 0.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">{row.customerName}</Typography>
                    <Chip
                      size="small"
                      color={row.approvedByAdminSampling ? 'success' : 'warning'}
                      label={
                        row.approvedByAdminSampling
                          ? 'Sudah disetujui admin sampling'
                          : 'Belum disetujui admin sampling'
                      }
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Jadwal: {row.scheduleId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Work order: {row.workOrderId ?? 'Belum ada'}
                  </Typography>
                  {row.approvedByAdminSampling ? (
                    <Typography variant="body2" color="text.secondary">
                      Tanggal: {row.approvedDatesLabel ?? 'Belum ada tanggal'}
                    </Typography>
                  ) : null}
                </Stack>
              </ListItemButton>
            ))}
          </List>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Tutup
        </Button>
      </DialogActions>
    </Dialog>
  );
}
